import axios from 'axios';
import logger from './logger';

/**
 * Session expiry — one 401 → logout path for every axios call (REQ-005, FE-5-11)
 * Location: frontend/src/utils/sessionExpiry.js
 *
 * services/api.js has its own instance (axios.create) with a 401 interceptor,
 * but 13 of 14 services call the default `axios` directly, so a revoked
 * session (e.g. a user disabled by an admin, BE-5-19) left pages rendering
 * empty results instead of returning to /login. installSessionExpiryInterceptor
 * registers the same handling once on the default instance.
 *
 * No double handling: interceptors on the default instance do not run for
 * instances made with axios.create, and both paths share `expireSession`'s
 * in-flight flag. No redirect loop: auth endpoints (a 401 there means bad
 * credentials, not an expired session) are skipped, as is any 401 when no user
 * is stored — which is also the state right after the first logout.
 */

const AUTH_ENDPOINT = /\/auth\//;
let loggingOut = false;
let installed = false;

const hasStoredUser = () => {
  try {
    return Boolean(localStorage.getItem('user'));
  } catch {
    return false;
  }
};

/** True while a session-expiry redirect is already under way. */
export const isSessionExpiring = () => loggingOut;

/**
 * Clear the stored session and send the user to /login, once.
 */
export const expireSession = () => {
  if (loggingOut) return;
  loggingOut = true;

  localStorage.removeItem('user');
  window.location.href = '/login';

  // A hard navigation normally unloads the page first; reset in case it does not.
  setTimeout(() => {
    loggingOut = false;
  }, 1000);
};

/**
 * Expire the session if `error` is a 401 from an authenticated, non-auth call.
 *
 * @param {Object} error - axios error
 * @returns {boolean} true if the session was expired
 */
export const handleUnauthorized = (error) => {
  if (error?.response?.status !== 401) return false;
  if (AUTH_ENDPOINT.test(error.config?.url || '')) return false;
  if (!hasStoredUser()) return false;

  logger.warn('401 on an authenticated request - session revoked or expired, logging out');
  expireSession();
  return true;
};

/**
 * Register the 401 handler on an axios instance (the default one unless given).
 * Idempotent for the default instance. The error is always re-thrown unchanged,
 * so callers' own error handling still runs.
 *
 * @param {Object} instance - axios instance
 */
export const installSessionExpiryInterceptor = (instance = axios) => {
  if (instance === axios) {
    if (installed) return;
    installed = true;
  }
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      handleUnauthorized(error);
      return Promise.reject(error);
    }
  );
};
